/** Default display style used across Securepay tables and detail views. */
export const SECUREPAY_DATETIME_DISPLAY_OPTIONS: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
};

export interface FormatSecurepayDateTimeDisplayOptions {
  locale?: string;
  fallback?: string;
  /** Overrides {@link SECUREPAY_DATETIME_DISPLAY_OPTIONS} when set. */
  dateTimeStyle?: Intl.DateTimeFormatOptions;
}

/**
 * Many APIs return UTC instants without a timezone suffix (e.g. `2024-06-01T12:00:00`).
 * This treats those as UTC by appending `Z` when no zone designator is present, then formats
 * with `toLocaleString` for consistent UI copy.
 */
export function formatSecurepayDateTimeDisplay(
  value: string | Date | null | undefined,
  options?: FormatSecurepayDateTimeDisplayOptions,
): string {
  const locale = options?.locale ?? 'en-US';
  const fallback = options?.fallback ?? 'N/A';
  const dateTimeStyle =
    options?.dateTimeStyle ?? SECUREPAY_DATETIME_DISPLAY_OPTIONS;

  if (value == null) {
    return fallback;
  }

  let instant: Date;
  if (value instanceof Date) {
    instant = value;
  } else {
    const raw = String(value).trim();
    if (!raw) {
      return fallback;
    }
    instant = new Date(ensureUtcDesignatorForNaiveApiString(raw));
  }

  if (Number.isNaN(instant.getTime())) {
    return fallback;
  }

  try {
    return instant.toLocaleString(locale, dateTimeStyle);
  } catch {
    return fallback;
  }
}

function ensureUtcDesignatorForNaiveApiString(s: string): string {
  if (/Z$/i.test(s) || /[+-]\d{2}:?\d{2}$/.test(s)) {
    return s;
  }
  return `${s}Z`;
}
